import clsx from 'clsx';
import { forwardRef, type ReactNode } from 'react';

import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';
import { Heading } from '../../typography/heading/Heading.js';

//#region Typings
/**
 * Accepted properties for the TitleBar Title
 * @public
 */
export interface TitleBarTitleProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the TitleBar Title slot. */
  children: ReactNode;
  /**
   * The semantic HTML heading element rendered by TitleBar.Title.
   * Use this to fit the TitleBar into your page's document outline.
   * @defaultValue 'h2'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * The visual heading size. Corresponds to the heading scale defined in the design system.
   * @defaultValue 3
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
}
//#endregion

/**
 * TitleBar Title component
 * @public
 */
export const Title = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  TitleBarTitleProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    as = 'h2',
    level = 3,
  }: TitleBarTitleProps = props;
  const childIsText = _canReceiveDefaultTextFormat(children);

  return (
    <div
      ref={forwardedRef}
      className={clsx(textStyleCSS({ textStyle: 'base' }), consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      {childIsText ? (
        <Heading level={level} as={as}>
          {children}
        </Heading>
      ) : (
        children
      )}
    </div>
  );
});
